/**
 * @typedef {Object} RoutineProduct
 * @property {string} id
 * @property {string} productId
 * @property {string} title
 * @property {string} image
 * @property {number} price
 * @property {string} [collectionId]
 * @property {string} [sellingPlanId]
 */

/**
 * @typedef {Object} Tier
 * @property {number} n - Item count threshold
 * @property {number} d - Discount percentage
 */

/**
 * @typedef {Object} PriceAdjustment
 * @property {string} value_type
 * @property {number} value
 */

/**
 * @typedef {Object} SellingPlan
 * @property {number} id
 * @property {string} name
 * @property {PriceAdjustment[]} [price_adjustments]
 */

/**
 * @typedef {Object} SellingPlanGroup
 * @property {SellingPlan[]} selling_plans
 */

/**
 * @typedef {Object} BuilderState
 * @property {Tier[]} tiers
 * @property {SellingPlanGroup[]} sellingPlanGroups
 * @property {'one' | 'sub'} purchaseMode
 */

/**
 * @typedef {Object} RechargeSDK
 * @property {Object} [cdn]
 * @property {(productId: string) => Promise<any>} [cdn.getCDNBundleSettings]
 * @property {Function} [cdn.validateBundle]
 * @property {Function} [cdn.getBundleId]
 * @property {Function} [cdn.getDynamicBundleItems]
 * @property {Object} [bundle]
 * @property {Function} [bundle.validateBundle]
 * @property {Function} [bundle.getBundleId]
 * @property {Function} [bundle.getDynamicBundleItems]
 * @property {Function} [validateBundle]
 * @property {Function} [getBundleId]
 * @property {Function} [getDynamicBundleItems]
 * @property {(options: any) => void} [init]
 * @property {Object} [auth]
 * @property {() => Promise<any>} [auth.loginShopifyAppProxy]
 * @property {Object} [tieredDiscount]
 * @property {Function} [tieredDiscount.listTieredDiscounts]
 * @property {Function} [tieredDiscount.getTieredDiscount]
 */

/**
 * Formats a dollar-value price using Shopify's money_format.
 * Falls back to a simple $X.XX if the format is unavailable.
 * @param {number} amount - Price in dollars (not cents)
 * @returns {string}
 */
function formatMoney(amount) {
  const moneyFormat = '${{amount}}';
  const rounded = amount.toFixed(2);
  return moneyFormat
    .replace('{{amount}}', rounded);
}

class RoutineBuilder extends HTMLElement {
  constructor() {
    super();
    /** @type {RoutineProduct[]} */
    this.items = [];
    /** @type {HTMLElement | null} */
    this.itemsContainer = null;
    /** @type {HTMLElement | null} */
    this.checkoutButton = null;
    /** @type {HTMLElement | null} */
    this.errorMessage = null;
    /** @type {NodeListOf<HTMLElement> | null} */
    this.tiers = null;
    /** @type {HTMLInputElement | null} */
    this.subscribeToggle = null;
    /** @type {string} */
    this.bundleProductId = '';
    /** @type {string} */
    this.bundleVariantId = '';

    /** @type {BuilderState} */
    this.state = {
      tiers: [],
      sellingPlanGroups: [],
      purchaseMode: 'one'
    };
    /** @type {Record<string, string>} */
    this.labels = {
      subtitle: 'Add your essentials here',
      mobileTitle: 'BUILD YOUR ROUTINE',
      addProductToStart: 'ADD A PRODUCT TO START',
      addToCart: 'ADD TO CART',
      adding: 'ADDING...',
      addAnItem: 'ADD AN ITEM',
      subscribeSave: 'SUBSCRIBE AND SAVE<br>AN ADDITIONAL 10%',
      itemsSuffix: 'ITEMS',
      offSuffix: '%OFF'
    };
    /** @type {number} */
    this.minItems = 3;
  }

  connectedCallback() {
    this.minItems = parseInt(this.dataset.minProducts || '3', 10);
    this.labels = {
      subtitle: this.dataset.labelSubtitle || 'Add your essentials here',
      mobileTitle: this.dataset.labelMobileTitle || 'BUILD YOUR ROUTINE',
      addProductToStart: this.dataset.labelAddProductToStart || 'ADD A PRODUCT TO START',
      addToCart: this.dataset.labelAddToCart || 'ADD TO CART',
      adding: this.dataset.labelAdding || 'ADDING...',
      addAnItem: this.dataset.labelAddAnItem || 'ADD AN ITEM',
      subscribeSave: this.dataset.labelSubscribeSave || 'SUBSCRIBE AND SAVE<br>AN ADDITIONAL 10%',
      itemsSuffix: this.dataset.labelItemsSuffix || 'ITEMS',
      offSuffix: this.dataset.labelOffSuffix || '%OFF'
    };

    this.itemsContainer = this.querySelector('.byb-sidebar__items');
    this.checkoutButton = this.querySelector('.byb-sidebar__checkout-btn');
    this.errorMessage = this.querySelector('[ref="errorMessage"]');
    this.tiers = this.querySelectorAll('.byb-sidebar__tier-box');

    this.mobileBar = this.querySelector('.byb-mobile-bar');
    this.mobileBarBtn = this.querySelector('.byb-mobile-bar__btn');
    this.mobileCloseBtn = this.querySelector('.byb-sidebar__mobile-close');
    this.mobileTitle = this.querySelector('.byb-sidebar__mobile-title');
    this.drawerOverlay = this.querySelector('.byb-drawer-overlay');

    this.subscribeToggle = this.querySelector('.byb-sidebar__toggle input');
    if (this.subscribeToggle instanceof HTMLInputElement) {
      this.subscribeToggle.checked = false; // Reset UI
      this.state.purchaseMode = 'one'; // Reset State
      this.subscribeToggle.addEventListener('change', (e) => {
        const target = e.target;
        if (target instanceof HTMLInputElement) {
          this.state.purchaseMode = target.checked ? 'sub' : 'one';
          this.render();
        }
      });
    }

    if (this.mobileBarBtn) {
      this.mobileBarBtn.addEventListener('click', () => this.openDrawer());
    }

    if (this.mobileCloseBtn) {
      this.mobileCloseBtn.addEventListener('click', () => this.closeDrawer());
    }

    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
    }

    this.init();

    // Parse custom tiered discounts from theme settings
    const tiersStr = this.dataset.tieredDiscounts || '3:3,4:5,5:7';
    const parsedTiers = tiersStr.split(',').map(pair => {
      const [qty, pct] = pair.split(':');
      return {
        tier: {
          condition_quantity_gte: parseInt(qty || '0', 10),
          discount_value: pct || '0'
        }
      };
    }).filter(t => t.tier.condition_quantity_gte > 0);
    this.applyTiers(parsedTiers);
  }

  openDrawer() {
    document.body.classList.add('byb-drawer-open');
  }

  closeDrawer() {
    document.body.classList.remove('byb-drawer-open');
  }



  /**
   * @param {any[]} tiers 
   */
  applyTiers(tiers) {
    this.state.tiers = Array.isArray(tiers)
      ? tiers
        .map(t => ({
          n: t.tier?.condition_quantity_gte || 0,
          d: parseFloat(t.tier?.discount_value || 0)
        }))
        .sort((a, b) => a.n - b.n)
      : [];

    this.renderTiers();
    this.render();
  }

  /**
   * @param {any} bundleData 
   */
  extractTieredDiscounts(bundleData) {
    const tieredDiscounts = bundleData.incentives?.tiered_discounts?.[0]?.tiers || [];
    this.applyTiers(tieredDiscounts);
  }

  /**
   * @returns {void}
   */
  renderTiers() {
    const containers = document.querySelectorAll('.byb-sidebar__tiers-container');
    if (containers.length === 0 || this.state.tiers.length === 0) return;

    containers.forEach(container => {
      container.innerHTML = '';
      this.state.tiers.forEach((tier, index) => {
        const tierEl = document.createElement('div');
        tierEl.className = 'byb-sidebar__tier';

        let label = `${tier.n} ${this.labels.itemsSuffix}`;
        if (index === this.state.tiers.length - 1 && index > 0) {
          label = `${tier.n}+ ${this.labels.itemsSuffix}`;
        }

        tierEl.innerHTML = `
          <span class="byb-sidebar__tier-discount">${tier.d}${this.labels.offSuffix}</span>
          <div class="byb-sidebar__tier-box byb-sidebar__tier-box--blue">${label}</div>
        `;
        container.appendChild(tierEl);
      });
    });

    this.updateTiers();
  }

  /**
   * @returns {void}
   */
  init() {
    // Listen for add button clicks globally on the document
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target instanceof Element) {
        const addButton = target.closest('[data-routine-add]');
        if (addButton instanceof HTMLElement) {
          this.addItem({
            id: addButton.dataset.variantId || addButton.dataset.productId || '',
            productId: addButton.dataset.productId || '',
            title: addButton.dataset.productTitle || '',
            image: addButton.dataset.productImage || '',
            price: parseFloat(addButton.dataset.productPrice || '0') || 0,
            collectionId: addButton.dataset.collectionId || '',
            sellingPlanId: addButton.dataset.productSellingPlanId || ''
          });
        }
      }
    });

    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', () => this.addAllToCart());
    }
  }

  /**
   * @param {RoutineProduct} product 
   */
  addItem(product) {
    this.clearError();
    this.items.push(product);
    this.render();

    if (window.innerWidth < 992) {
      this.openDrawer();
    }
  }

  /**
   * @param {number | string} index 
   * @returns {void}
   */
  removeItem(index) {
    this.clearError();
    this.items.splice(Number(index), 1);
    this.render();
  }

  /**
   * @param {number} itemCount 
   * @returns {Tier | null}
   */
  getTier(itemCount) {
    let activeTier = null;
    for (const tier of this.state.tiers) {
      if (itemCount >= tier.n) {
        activeTier = tier;
      }
    }
    return activeTier;
  }

  /**
   * @returns {number}
   */
  getSubscriptionDiscountPercent() {
    return parseFloat(this.dataset.sellingPlanDiscount || '10');
  }

  /**
   * @param {number} originalPrice 
   * @param {number} itemCount 
   * @returns {number}
   */
  getDiscountedPrice(originalPrice, itemCount) {
    const tier = this.getTier(itemCount);
    const tieredDiscountPercent = tier ? tier.d : 0;

    let currentPrice = originalPrice;

    if (tieredDiscountPercent > 0) {
      currentPrice = currentPrice * (1 - (tieredDiscountPercent / 100));
    }

    if (this.state.purchaseMode === 'sub') {
      const subPercent = this.getSubscriptionDiscountPercent();
      currentPrice = currentPrice * (1 - (subPercent / 100));
    }

    return currentPrice;
  }

  /**
   * @param {number} subtotal 
   * @param {number} itemCount 
   * @returns {{ original: number, total: number }}
   */
  calculatePricing(subtotal, itemCount) {
    const tier = this.getTier(itemCount);

    const tieredDiscountPercent = tier ? tier.d : 0;
    const tieredDiscount = subtotal * (tieredDiscountPercent / 100);
    const afterTieredDisc = subtotal - tieredDiscount;

    const subPercent = this.getSubscriptionDiscountPercent();
    const subscriptionDiscount = this.state.purchaseMode === 'sub'
      ? (afterTieredDisc * (subPercent / 100))
      : 0;

    const total = afterTieredDisc - subscriptionDiscount;

    return {
      original: subtotal,
      total: total
    };
  }

  /**
   * @returns {void}
   */
  render() {
    const container = this.itemsContainer;
    if (!container) return;

    container.innerHTML = '';
    let subtotal = 0;

    // Render filled items
    this.items.forEach((item, index) => {
      subtotal += Number(item.price) || 0;

      const originalItemPrice = Number(item.price) || 0;
      const discountedItemPrice = this.getDiscountedPrice(originalItemPrice, this.items.length);
      const hasDiscount = originalItemPrice > discountedItemPrice;

      let priceHTML = '';
      if (hasDiscount) {
        priceHTML = `
          <span class="byb-sidebar__item-price">${formatMoney(discountedItemPrice)}</span>
          <span class="byb-sidebar__item-compare">${formatMoney(originalItemPrice)}</span>
        `;
      } else {
        priceHTML = `<span class="byb-sidebar__item-price">${formatMoney(originalItemPrice)}</span>`;
      }

      const itemElement = document.createElement('div');
      itemElement.className = 'byb-sidebar__item';
      itemElement.innerHTML = `
        <div class="byb-sidebar__item-img-wrapper">    
          <img src="${item.image || ''}" class="byb-sidebar__item-img" alt="${item.title || ''}">
        </div>
        <div class="byb-sidebar__item-info">
          <span class="byb-sidebar__item-title">${item.title || ''}</span>
          <div class="byb-sidebar__item-prices">
            ${priceHTML}
          </div>
        </div>
        <button class="byb-sidebar__item-remove button-unstyled" data-index="${index}">
         <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3 6H21M19 6V20C19 21 18 22 17 22H7C6 22 5 21 5 20V6M8 6V4C8 3 9 2 10 2H14C15 2 16 3 16 4V6M10 11V17M14 11V17" stroke="#242424" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

        </button>
      `;
      container.appendChild(itemElement);
    });

    const pricing = this.calculatePricing(subtotal, this.items.length);

    // Render empty slots (always show minimum)
    const emptySlotsCount = Math.max(0, this.minItems - this.items.length);
    for (let i = 0; i < emptySlotsCount; i++) {
      const emptyElement = document.createElement('div');
      emptyElement.className = 'byb-sidebar__item-empty';
      emptyElement.innerHTML = `
          <div class="byb-sidebar__item-empty-img"></div>
          <span class="byb-sidebar__item-empty-text">${this.labels.addAnItem}</span>
          <button class="byb-sidebar__item-add-btn button-unstyled">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 2C8.33333 2 15.6667 2 22 2V22C15.6667 22 8.33333 22 2 22C2 15.6667 2 8.33333 2 2Z" fill="#ECF6FF" stroke="black" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M12 8V16M16 12H8" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        `;
      container.appendChild(emptyElement);
    }

    // Update Footer button
    /** @param {HTMLElement | Element | null | undefined} btn */
    const updateButtonText = (btn) => {
      if (!btn) return;
      if (this.items.length >= this.minItems) {
        btn.classList.add('is-ready');
      } else {
        btn.classList.remove('is-ready');
      }

      if (this.items.length === 0) {
        btn.textContent = `0/${this.minItems} ${this.labels.addProductToStart}`;
      } else if (this.items.length < this.minItems) {
        btn.textContent = `${this.items.length}/${this.minItems} ${this.labels.addProductToStart}`;
      } else {
        const hasDiscount = pricing.original > pricing.total;
        if (hasDiscount) {
          btn.innerHTML = `${this.labels.addToCart} - ${formatMoney(pricing.total)} <s style="opacity: 0.5;">${formatMoney(pricing.original)}</s> `;
        } else {
          btn.innerHTML = `${this.labels.addToCart} - ${formatMoney(pricing.total)}`;
        }
      }
    };

    updateButtonText(this.checkoutButton);
    updateButtonText(this.mobileBarBtn);

    if (this.mobileTitle) {
      const denominator = Math.max(this.items.length, this.minItems);
      this.mobileTitle.textContent = `${this.labels.mobileTitle} (${this.items.length}/${denominator} SELECTED)`;
    }

    // Re-attach remove listeners
    container.querySelectorAll('.byb-sidebar__item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.target;
        if (target instanceof Element) {
          const button = target.closest('.byb-sidebar__item-remove');
          if (button instanceof HTMLElement && button.dataset.index !== undefined) {
            this.removeItem(button.dataset.index);
          }
        }
      });
    });

    this.updateTiers();
    this.updateGridPrices();
  }

  /**
   * @returns {void}
   */
  updateGridPrices() {
    const externalItems = document.querySelectorAll('.product-grid__item');
    externalItems.forEach(item => {
      if (!(item instanceof HTMLElement)) return;

      // Find all ref="priceContainer" elements (for both mobile and desktop)
      let priceContainers = Array.from(item.querySelectorAll('[ref="priceContainer"]'));

      // Fallback to .price if no ref="priceContainer" is found
      if (priceContainers.length === 0) {
        const fallback = item.querySelector('.price');
        if (fallback) priceContainers = [fallback];
      }

      if (priceContainers.length === 0) return;

      const originalPrice = parseFloat(item.dataset.productPrice || '0');
      if (originalPrice === 0) return;

      const discountedPrice = this.getDiscountedPrice(originalPrice, this.items.length);
      const hasDiscount = originalPrice > discountedPrice;

      const htmlContent = hasDiscount ? `
          <span role="group">
            <span class="visually-hidden">Regular price&nbsp;</span>
            <span class="compare-at-price" style="text-decoration: line-through; margin-right: 8px;">${formatMoney(originalPrice)}</span>
          </span>
          <span role="group">
            <span class="visually-hidden">Sale price&nbsp;</span>
            <span class="price">${formatMoney(discountedPrice)}</span>
          </span>
        ` : `
          <span class="price">${formatMoney(originalPrice)}</span>
        `;

      priceContainers.forEach(container => {
        container.innerHTML = htmlContent;
      });
    });
  }

  /**
   * @returns {void}
   */
  updateTiers() {
    const containers = document.querySelectorAll('.byb-sidebar__tiers-container');
    if (containers.length === 0 || this.state.tiers.length === 0) return;

    let activeIndex = 0;
    this.state.tiers.forEach((tier, index) => {
      if (this.items.length >= tier.n) {
        activeIndex = index;
      }
    });

    containers.forEach(container => {
      const tierBoxes = container.querySelectorAll('.byb-sidebar__tier-box');
      tierBoxes.forEach((tier, index) => {
        if (index === activeIndex) {
          tier.classList.remove('byb-sidebar__tier-box--blue');
          tier.classList.add('byb-sidebar__tier-box--white');
        } else {
          tier.classList.remove('byb-sidebar__tier-box--white');
          tier.classList.add('byb-sidebar__tier-box--blue');
        }
      });
    });
  }

  /**
   * @returns {Promise<void>}
   */
  async addAllToCart() {
    if (this.items.length < this.minItems) {
      return;
    }

    try {
      if (this.checkoutButton) {
        this.checkoutButton.textContent = this.labels.adding || 'ADDING...';
      }
      this.clearError();

      /** @type {Record<string, { collectionId: string, externalProductId: string, externalVariantId: string, quantity: number, sellingPlanId?: string }>} */
      const grouped = this.items.reduce((acc, item) => {
        const key = item.id;
        if (!acc[key]) {
          acc[key] = {
            collectionId: String(item.collectionId || ''),
            externalProductId: String(item.productId),
            externalVariantId: String(item.id),
            quantity: 0,
            sellingPlanId: item.sellingPlanId || ''
          };
        }
        acc[key].quantity += 1;
        return acc;
      }, /** @type {Record<string, any>} */({}));
      const selections = Object.values(grouped);

      const cartItems = selections.map(sel => {
        let planId = undefined;
        if (this.state.purchaseMode === 'sub') {
          planId = sel.sellingPlanId ? parseInt(sel.sellingPlanId) : undefined;
        }
        return {
          id: parseInt(sel.externalVariantId),
          quantity: sel.quantity,
          selling_plan: planId
        };
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });

      if (response.ok) {
        this.reset();
        (/** @type {any} */ (window)).Rebuy?.SmartCart?.show();
      } else {
        let errorMessage = 'Failed to add items to cart';

        try {
          const errorData = await response.json();
          console.error('Cart add error:', errorData);
          // Try a few common fields in order of usefulness
          if (errorData.description) {
            errorMessage = errorData.description;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.status) {
            errorMessage = `Error ${errorData.status}: Unable to add items to cart`;
          }
        } catch (e) {
          console.error('Could not parse /cart/add.js error JSON', e);
        }
        this.showError(errorMessage);
        this.render();
      }
    } catch (error) {
      if (this.checkoutButton) {
        this.checkoutButton.textContent = 'ERROR - TRY AGAIN';
      }
      // @ts-ignore
      this.showError(error.message);
      this.render();
    }
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.classList.remove('hidden');
    }
  }

  clearError() {
    if (this.errorMessage) {
      this.errorMessage.textContent = '';
      this.errorMessage.classList.add('hidden');
    }
  }

  reset() {
    this.items = [];
    this.state.purchaseMode = 'one';
    if (this.subscribeToggle) {
      this.subscribeToggle.checked = false;
    }

    this.clearError();
    this.render();
    this.closeDrawer()

  }
}

if (!customElements.get('build-your-box-sidebar')) {
  customElements.define('build-your-box-sidebar', RoutineBuilder);
}